import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { motion, useInView } from "framer-motion";
import Footer from "../components/Footer";
import Header from "../components/Header";
import "./AboutUs.scss";
import AboutHistory from "../components/AboutHistory";
import OurAssociationsVelocity from "../components/OurAssociationsVelocity";
import imgHimanshu from "../assets/leader-himanshu.png";
import imgSaurabh from "../assets/leader-saurabh.png";
import media1 from "../assets/media/1.webp";
import media2 from "../assets/media/2.webp";
import media5 from "../assets/media/5.webp";
import media7 from "../assets/media/7.webp";
import media9 from "../assets/media/9.webp";
import media10 from "../assets/media/10.webp";
import media from "../assets/media/media_11zon.webp";
import { FaLinkedin, FaBullseye, FaEye, FaUsers } from "react-icons/fa";
import { ArrowUpRight } from "iconoir-react";
import AsOfNote from "../components/AsOfNote";
import { impact } from "../data/stats";

// ── Scroll-triggered fade-up wrapper ─────────────────────────
const FadeUp = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.62, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

// ── Video card with lazy-load thumbnail ───────────────────────
const VideoCard = ({ video }) => {
  const [playing, setPlaying] = useState(false);
  const ytLink = `https://www.youtube.com/watch?v=${video.id}`;
  const embedSrc = `https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0&playsinline=1&modestbranding=1`;

  return (
    <div className="au-vid-card">
      <div
        className={`au-vid-thumb${playing ? " au-vid-thumb--playing" : ""}`}
        onClick={() => !playing && setPlaying(true)}
      >
        {playing ? (
          <iframe
            src={embedSrc}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <>
            <img
              src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
              alt={video.title}
            />
            <div className="au-vid-play">
              <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </>
        )}
      </div>
      <div className="au-vid-meta">
        <span className="au-vid-platform">{video.platform}</span>
        <h4 className="au-vid-title">{video.title}</h4>
        <p className="au-vid-desc">{video.desc}</p>
        <a
          href={ytLink}
          target="_blank"
          rel="noopener noreferrer"
          className="au-vid-yt"
        >
          Watch on YouTube ↗
        </a>
      </div>
    </div>
  );
};

// ── Data ──────────────────────────────────────────────────────
const mvtCards = [
  {
    label: "Our Mission",
    icon: FaBullseye,
    text: "To deliver quality education that is innovative, affordable, and accessible to every student across India — no matter where they live.",
  },
  {
    label: "Our Vision",
    icon: FaEye,
    text: "To reshape learning with cutting-edge technology and personalized support, so every child gets the attention they deserve.",
  },
  {
    label: "Our Team",
    icon: FaUsers,
    text: "A dedicated team of educators and builders, united by one goal — making a meaningful difference in education every single day.",
  },
];

const founders = [
  {
    name: "Himanshu Chaurasia",
    role: "Co-Founder & CEO",
    bio: "With an unwavering vision to build world-class schools across India, Himanshu leads Cograd's strategy, culture, and long-term direction — from the first campus to national scale.",
    linkedin: "https://www.linkedin.com/in/chaurasia-himanshu/",
    image: imgHimanshu,
  },
  {
    name: "Saurabh Yadav",
    role: "Co-Founder & COO",
    bio: "Joining at the earliest stages, Saurabh drives Cograd's operational excellence — from school onboarding and partnerships to flawless ground-level execution across India.",
    linkedin: "https://www.linkedin.com/in/saurabh-yadav-8048a013b/",
    image: imgSaurabh,
  },
];

const mediaItems = [
  { id: 1, image: media7, alt: "Cograd on Money Control", label: "Money Control", link: "https://www.moneycontrol.com/entertainment/shark-tank-india-4-cograd-founders-questioned-for-lack-of-focus-secure-rs-1-crore-deal-amid-tough-negotiations-article-12951529.html/amp" },
  { id: 2, image: media2, alt: "Cograd on India Times",   label: "India Times",   link: "https://www.indiatimes.com/hindi/entrepreneurship/leaving-the-package-of-lakh-two-friend-grooming-the-future-of-children-569390.html" },
  { id: 3, image: media10, alt: "Cograd on ZeeBiz",      label: "Zee Business",  link: "https://www.zeebiz.com/startups/news-shark-tank-india-4-this-education-startup-tells-ritesh-agarwal-they-are-oyo-of-schools-bags-three-sharks-deal-348962" },
  { id: 4, image: media9, alt: "Cograd on News 18",      label: "News18",        link: "https://www.news18.com/movies/shark-tank-india-vineeta-singh-goes-silent-as-ritesh-agarwal-ignores-her-in-front-of-pitchers-9244108.html" },
  { id: 5, image: media5, alt: "Cograd on Josh Talks",   label: "Josh Talks",    link: "https://youtu.be/xP6glTTCXKk?si=jY5T7r_wv4yWkdI4" },
  { id: 6, image: media1, alt: "Cograd on Your Story",   label: "YourStory",     link: "https://yourstory.com/hindi/this-startup-is-giving-hi-tech-education-to-children-in-villages-cograd" },
];

const videos = [
  {
    id: "jMkCnEDCJo8",
    platform: "Shark Tank India",
    title: "Cograd Secures ₹1 Crore Deal",
    desc: "Watch our founders pitch Cograd to the sharks and secure a landmark ₹1 Crore investment from three investors.",
  },
  {
    id: "jZAr0WVOsGI",
    platform: "Documentary",
    title: "The Cograd Story",
    desc: "A deep look into how Cograd is bringing high-quality, tech-enabled education to India's heartland.",
  },
  {
    id: "xP6glTTCXKk",
    platform: "Josh Talks",
    title: "Changing Education in Rural India",
    desc: "Himanshu shares the journey of building Cograd — challenges, turning points, and the vision ahead.",
  },
  {
    id: "_0frSMZOnyA",
    platform: "Feature",
    title: "Medha AI — Future of Learning",
    desc: "How Medha AI is personalizing education for students across 200+ villages in rural India.",
  },
  {
    id: "RLCRm3QvMm0",
    platform: "Inside Story",
    title: "Inside a Cograd School",
    desc: "A look inside the innovative school environment Cograd has built for students across Uttar Pradesh.",
  },
  {
    id: "Qh8ip-3QHTk",
    platform: "Interview",
    title: "Building India's Future Schools",
    desc: "Two founders on disrupting traditional schooling in rural India with innovation, technology, and purpose.",
  },
];

// ── Page ──────────────────────────────────────────────────────
const AboutUs = () => {
  return (
    <div className="au-page">
      <Helmet>
        <title>About Cograd - Innovative and Affordable Education</title>
        <meta
          name="description"
          content="Discover the vision of Cograd's founders for building accessible and innovative schools in India. Learn about our journey, services, and partnership."
        />
      </Helmet>
      <Header />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="au-hero">
        <div className="au-container au-hero-inner">
          <FadeUp>
            <div className="au-hero-badges">
              <div className="au-hero-badge">
                <span className="au-hero-badge-dot" />
                Funded on Shark Tank India · Season 4
              </div>
              <span className="au-label">Est. 2019 · About Cograd</span>
            </div>
            <h1 className="au-hero-heading">
              Transforming Education
              <br />
              <span className="au-hero-grad">Across India</span>
            </h1>
            <p className="au-hero-sub">
              Building innovative, affordable schools that unlock the potential
              of every student — from villages to cities, with technology and purpose.
            </p>
            <div className="au-hero-ctas">
              <a href="#founders" className="au-hero-cta-primary">Meet Our Founders</a>
              <a href="#journey" className="au-hero-cta-ghost">
                Our Journey <ArrowUpRight className="au-cta-icon" />
              </a>
            </div>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="au-stats-row">
              {[
                { num: impact.schools.display,   label: impact.schools.label   },
                { num: impact.students.display,  label: impact.students.label  },
                { num: impact.incubated.display, label: impact.incubated.label },
                { num: impact.years.display,     label: impact.years.label     },
              ].map((s) => (
                <div key={s.label} className="au-stat">
                  <span className="au-stat-num">{s.num}</span>
                  <span className="au-stat-label">{s.label}</span>
                </div>
              ))}
            </div>
            <AsOfNote className="asof--center" />
          </FadeUp>
        </div>
      </section>

      {/* ── OUR JOURNEY ──────────────────────────────────────── */}
      <section id="journey" className="au-journey-section">
        <AboutHistory />
      </section>

      {/* ── MISSION / VISION / TEAM ──────────────────────────── */}
      <section className="au-mvt">
        <div className="au-container">
          <FadeUp className="au-section-header">
            <span className="au-label">What We Stand For</span>
            <h2 className="au-section-heading">Mission, Vision &amp; Team</h2>
            <p className="au-section-sub">The principles that shape everything we build.</p>
          </FadeUp>
          <div className="au-mvt-grid">
            {mvtCards.map((card, i) => {
              const Icon = card.icon;
              return (
                <FadeUp key={card.label} delay={i * 0.1}>
                  <div className="au-mvt-card">
                    <span className="au-mvt-num">0{i + 1}</span>
                    <div className="au-mvt-icon">
                      <Icon />
                    </div>
                    <h3>{card.label}</h3>
                    <p>{card.text}</p>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FOUNDERS ─────────────────────────────────────────── */}
      <section id="founders" className="au-founders">
        <div className="au-container">
          <FadeUp className="au-section-header">
            <span className="au-label">Leadership</span>
            <h2 className="au-section-heading">Our Founders</h2>
            <p className="au-section-sub">The people behind Cograd's mission to revolutionize Indian education.</p>
          </FadeUp>
          <div className="au-founders-grid">
            {founders.map((f, i) => (
              <FadeUp key={f.name} delay={i * 0.1}>
                <div className="au-founder-card">
                  <div className="au-founder-photo">
                    <img src={f.image} alt={f.name} />
                  </div>
                  <div className="au-founder-body">
                    <span className="au-founder-role-badge">{f.role}</span>
                    <h3 className="au-founder-name">{f.name}</h3>
                    <p className="au-founder-bio">{f.bio}</p>
                    <a
                      href={f.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="au-founder-link"
                    >
                      <FaLinkedin />
                      <span>Connect on LinkedIn</span>
                      <ArrowUpRight />
                    </a>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── ASSOCIATIONS ─────────────────────────────────────── */}
      <OurAssociationsVelocity />

      {/* ── MEDIA COVERAGE ───────────────────────────────────── */}
      <section className="au-media">
        <div className="au-container">
          <FadeUp className="au-section-header">
            <span className="au-label">Media &amp; Press</span>
            <h2 className="au-section-heading">As Featured In</h2>
            <p className="au-section-sub">Our story, covered by India's leading publications.</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="au-media-layout">
              <div className="au-media-featured">
                <img src={media} alt="Cograd in news" />
              </div>
              <div className="au-media-small-grid">
                {mediaItems.map((item) => (
                  <a
                    key={item.id}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="au-media-thumb"
                  >
                    <img src={item.image} alt={item.alt} />
                    <div className="au-media-thumb-label">{item.label}</div>
                    <div className="au-media-thumb-btn">
                      <ArrowUpRight />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── VIDEOS ───────────────────────────────────────────── */}
      <section className="au-videos">
        <div className="au-container">
          <FadeUp className="au-section-header">
            <span className="au-label">Watch &amp; Learn</span>
            <h2 className="au-section-heading">Cograd in Action</h2>
            <p className="au-section-sub">From Shark Tank to Josh Talks — our story in motion.</p>
          </FadeUp>
          <div className="au-videos-grid">
            {videos.map((video, i) => (
              <FadeUp key={video.id} delay={i * 0.07}>
                <VideoCard video={video} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
